import React, { useContext, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Typography, Select, MenuItem, FormControl, TextField, Modal } from "@mui/material";
import submitIcon from "../../../assets/images/submitIcon.svg";
import CustomButton from "../../components/buttons/CustomButton";
import { SelectedAlertsContext } from "../../../utils/SelectedAlertContext";
import { executeActionAPI } from "../../api/api";
import { contractAgingAPI } from "../../api/ContractConverageAPI";
import styles from "./popup.module.css";
import { useMsal } from "@azure/msal-react";
import SnackBarMessage from "../../components/snackbar/snackbar";

const options = [
  "Lag in data",
  "Job works location inventory",
  "Scheduling Issues",
  "QC stock issue",
  "PV issue",
  "Shelf-life master issue",
  "Lead time master issue",
  "STO stock issue",
  "GRN Pending",
  "Material Inactive/Wrong",
  "Incorrect Demand",
  "Stock Available",
  "Other reasons",
];

const contractOptions = [
  "Wrong Buyer mapping",
  "Wrong Agreement No.",
  "Alternate Vendor Present",
  "Incorrect PRs",
  "IUT Contract",
  "Delisted Material",
  "Wrong Agreement line item no.",
  "Wrong PV Selected",
  "2P-3P Plant",
  "Other reasons",

];

const agileOrOptions = [
  "Incorrect Capacity Masters",
  "Incorrect Priority",
  "Lag in production booking"
];

const defaultOptions = [
  "Option 1", "Option 2", "Option 3"
]

const getRejectionOptionsByType = (type) => {
  if (type === "Contract Alert") {
    return contractOptions;
  }
  if (type === "Agile OR") {
    return agileOrOptions;
  }
  if (type === "RMPM Alert") {
    return options;
  }
  return defaultOptions;
};

const getAlertTypeKey = (type) => {
  if (type === "RMPM Alert") {
    return "rmpm";
  }
  if (type === "Contract Alert") {
    return "contract";
  }
  return "agileOr";
};

const getConfirmationTitle = (status) => (status === "Rejected" ? "Rejection" : "Acceptance");

const getActionLabel = (status) => (status === "Rejected" ? "reject" : "accept");

const getActionStatus = (status) => (status === "Rejected" ? "Rejected" : "Accepted");

const getUpdatedAlertDetailsItem = (item, alertsToProcess, type, actionStatus,reason, updateRmpmAlertItem, updateOtherAlertItem) => {
  const processedAlert = alertsToProcess.find(
    (alert) => alert["Alert ID"] === item["Alert ID"]
  );

  if (!processedAlert) {
    return item;
  }

  if (type === "RMPM Alert") {
    return updateRmpmAlertItem(item, processedAlert, actionStatus, reason);
  }

  return updateOtherAlertItem(item, processedAlert, actionStatus);
};


const ActionPopup = ({ open, onClose, onConfirm, type = '', onAccept, status = '' }) => {
  const { accounts } = useMsal();
  const activeAccount = accounts[0] || null;
  const {
    setAlertDetailsData,
    selections,
    setSelections
  } = useContext(SelectedAlertsContext);

  const [selectedReason, setSelectedReason] = useState("");
  const [openInput, setOpenInput] = useState(false);
  const [customReason, setCustomReason] = useState("");
  const [snackBarObject, setSnackBarObject] = useState({
          snackbarOpen: false,
          message: ' ',
          type: 'error'
      });

  const rejectionOptions = getRejectionOptionsByType(type);

  const alertTypeKey = getAlertTypeKey(type);

  // Get the alerts to process based on status
  const alertsToProcess = status === 'Rejected'
    ? selections[alertTypeKey]?.rejected || []
    : selections[alertTypeKey]?.accepted || [];

  // Get alert IDs for display
  const alertIds = alertsToProcess.map(alert => alert["Alert ID"]);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (!open) {
      setSelectedReason("");
      setCustomReason("");
      setOpenInput(false);
    }
  }, [open]);

  // Helper to validate RMPM alerts before acceptance
  const validateRmpmAlerts = () => {
    return alertsToProcess.every(
      (alert) =>
        alert["Recommendation"] !== "Other Recommendation" ||
        (alert["selectedRecom"]?.[0]?.Title === "Other Recommendation" &&
          alert["selectedRecom"]?.[0]?.Info !== "") ||
        (alert["Recommendation"] === "Other Recommendation" &&
          alert["selectedRecom"]?.[0]?.Title !== "Other Recommendation" &&
          alert["selectedRecom"]?.[0]?.Info === "")
    );
  };

  // Helper to process Contract Alerts API calls
  const processContractAlerts = async (actionStatus, reason) => {
    const apiPromises = alertsToProcess.map((alert) => {
      const reqObj = {
        alertIds: [alert["Alert ID"]],
        materialId: alert["Material ID"],
        agreementNo: alert["Agreement no."],
        vendorId: alert["Vendor ID"],
        plant: alert["Site code"],
        Alert_Status: actionStatus,
        reason: status === "Rejected" ? reason : undefined,
      };
      return contractAgingAPI(reqObj);
    });
    await Promise.all(apiPromises);
  };

  // Helper to get recommendation value for RMPM alerts
  const getRmpmRecommendation = (item, processedAlert, actionStatus) => {
    if (actionStatus === "Rejected") return item["RecommendationOld"];
    const recomInfo = processedAlert.selectedRecom?.[0]?.Info;
    return recomInfo !== "" ? recomInfo : processedAlert.selectedRecom[0].Title;
  };

  // Helper to update RMPM alert item
  const updateRmpmAlertItem = (item, processedAlert, actionStatus, reason) => {
    const baseUpdate = {
      ...item,
      "Alert status": actionStatus,
      BuyerIntervention: processedAlert["BuyerIntervention"],
      [actionStatus === "Accepted" ? "RecommendationNew" : "Recommendation"]:
        getRmpmRecommendation(item, processedAlert, actionStatus),
      ...(actionStatus === "Rejected" ? { FactoryComments: reason } : {}),
    };
    return actionStatus === "Accepted"
      ? baseUpdate
      : { ...baseUpdate};
  };

  // Helper to update non-RMPM alert item
  const updateOtherAlertItem = (item, processedAlert, actionStatus) => {
    const recomInfo = processedAlert.selectedRecom?.[0]?.Info;
    const recomTitle = processedAlert.selectedRecom?.[0]?.title;
    const fallback =
      actionStatus === "Rejected" ? item["RecommendationOld"] : item["Recommendation"];
    const recommendation =
      recomInfo === item["Recommendation"]
        ? item["Recommendation"]
        : recomTitle ?? fallback;

    return { ...item, "Alert status": actionStatus, Recommendation: recommendation };
  };

  // Helper to update alert details data
  const updateAlertDetailsData = (actionStatus, reason) => {
    setAlertDetailsData((prevData) =>
      prevData.map((item) =>
        getUpdatedAlertDetailsItem(
          item,
          alertsToProcess,
          type,
          actionStatus,
          reason,
          updateRmpmAlertItem,
          updateOtherAlertItem
        )
      )
    );
  };

  // Helper to update selections after action
  const updateSelections = () => {
    if (status === "Rejected") {
      setSelections((prev) => ({
        ...prev,
        [alertTypeKey]: { ...prev[alertTypeKey], rejected: [] },
      }));
    } else {
      setSelections((prev) => ({
        ...prev,
        [alertTypeKey]: {
          ...prev[alertTypeKey],
          accepted: prev[alertTypeKey]?.accepted.filter(
            (item) => !alertIds.includes(item["Alert ID"])
          ),
        },
      }));
    }
  };

  const handleSubmit = async () => {
    if (onConfirm) onConfirm();
    onClose();

    try {
      const actionStatus = getActionStatus(status);
      const reason = customReason || selectedReason;

      if (type === "Contract Alert") {
        await processContractAlerts(actionStatus, reason);
      } else {
        if (actionStatus === "Accepted" && type === "RMPM Alert" && !validateRmpmAlerts()) {
          setSnackBarObject({
            snackbarOpen: true,
            message: 'Please enter action in "Other Recommendation" to accept the alert',
            type: "error",
          });
          return;
        }
        await executeActionAPI(
          alertIds,
          actionStatus,
          reason,
          type,
          alertsToProcess,
          activeAccount.username
        );
      }

      updateAlertDetailsData(actionStatus, reason);
      updateSelections();

      if (onAccept) onAccept(alertIds.length);
      onClose();
    } catch (error) {
      console.error("Error executing actions:", error);
    }
  };

  useEffect(() => {
    if (selectedReason === "Other reasons") {
      setOpenInput(true);
      setCustomReason("");
    } else {
      setOpenInput(false);
    }
  }, [selectedReason]);

  return (
    <>
     {snackBarObject.snackbarOpen && (
                  <SnackBarMessage
                    type={snackBarObject.type}
                    snackbarMessage={snackBarObject.message}
                    snackbarOpen={snackBarObject.snackbarOpen}
                    setSnackbarOpen={()=>setSnackBarObject({snackbarOpen:false,message: ' ',type: 'error'})}
                  />
      )}
    <Modal open={open} onClose={onClose}>
      <div className={styles.modalContainer}>
        <div className={styles.dialogBox}>
          <div className={styles.dialogBoxContent}>
            <img src={submitIcon} alt="Submit icon" width={40} height={40} />
            <Typography className={styles.header}>
              Confirm {getConfirmationTitle(status)}
            </Typography>
          </div>

          <Typography className={styles.subHeader}>
            Are you sure you want to {getActionLabel(status)} the alerts? Once
            {status === 'Rejected' ? ' rejected' : ' accepted'}, the data cannot be modified.
          </Typography>

          <div className={styles.alertList}>
            <Typography className={styles.alertTitle}>Alert ID's:</Typography>
            {alertIds.map((alert) => (
              <div key={`alert-${String(alert)}`} className={styles.alertItem}>
                <Typography className={styles.alertId}>• {alert}</Typography>
              </div>
            ))}
          </div>

          {status === 'Rejected' && (
            <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
              <Typography className={styles.alertTitle}>
                Please select the reason for rejection:
              </Typography>
              <Select
                value={selectedReason}
                onChange={(e) => setSelectedReason(e.target.value)}
                size="small"
                className={styles.rejectedDropdown}
              >
                {rejectionOptions.map((option) => (
                  <MenuItem className={styles.listItem} key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
              {selectedReason === "Other reasons" && openInput && (
                <FormControl style={{ width: "100%" }}>
                  <TextField
                    size="small"
                    autoFocus
                    autoComplete="off"
                    value={customReason}
                    onChange={(e) => setCustomReason(e.target.value)}
                    placeholder="Please enter your reason"
                    inputProps={{ style: { fontSize: "12px", width: "100%" } }}
                  />
                </FormControl>
              )}
            </div>
          )}

          <div className={styles.dialogBtn}>
            <CustomButton label="Cancel" type="popupCancel" onClick={onClose} />
            <CustomButton
              label={status === 'Rejected' ? 'Reject' : 'Accept'}
              type="popupSubmit"
              onClick={handleSubmit}
              disabled={status === 'Rejected' && !selectedReason}
            />
          </div>
        </div>
      </div>
    </Modal>
    </>
  );
};

ActionPopup.propTypes = {
  onAccept: PropTypes.func,
  open: PropTypes.bool,
  type: PropTypes.string,
  status: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func,
};

export default ActionPopup;